import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { projects } from '../data/projects';
import { useTranslation } from 'react-i18next';
import SteamVideo from './SteamVideo';

function ProjectDetail() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0];
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find(p => p.id === parseInt(id));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getLocalizedText = useCallback((value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'object') {
      return value[currentLang] || value.en || value.tr || fallback;
    }
    return value;
  }, [currentLang]);

  useEffect(() => {
    // Sayfa yüklendiğinde yukarı kaydır
    window.scrollTo(0, 0);
    setCurrentImageIndex(0);
  }, [id]);

  useEffect(() => {
    if (!project) {
      navigate('/', { replace: true });
      return;
    }

    const title = getLocalizedText(project.title);
    const description = getLocalizedText(project.description);
    document.title = `${title} | Nuri Onacak Portfolio`;

    [
      ['meta[name="description"]', description],
      ['meta[property="og:title"]', `${title} | Nuri Onacak`],
      ['meta[property="og:description"]', description],
      ['meta[property="og:image"]', `https://nurionacak.com${project.image}`],
      ['meta[property="twitter:title"]', `${title} | Nuri Onacak`],
      ['meta[property="twitter:description"]', description],
      ['meta[property="twitter:image"]', `https://nurionacak.com${project.image}`]
    ].forEach(([selector, value]) => {
      const element = document.querySelector(selector);
      if (element && value) element.setAttribute('content', value);
    });
  }, [project, navigate, currentLang, getLocalizedText]);

  const handleGoBack = () => {
    navigate('/');
    setTimeout(() => {
      const projectsSection = document.getElementById('projects');
      projectsSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  if (!project) {
    return null;
  }

  // Projeler sayfasındaki seviye numarasıyla aynı sıralama
  const levelNumber =
    [...projects]
      .sort((a, b) => new Date(a.date || '2000-01-01') - new Date(b.date || '2000-01-01'))
      .findIndex(p => p.id === project.id) + 1;

  // Video ve resimleri birleştir (tüm videolar ilk sırada)
  const allMedia = [];
  if (project.videoUrl) {
    allMedia.push({ type: 'video', url: project.videoUrl, format: project.videoType, poster: project.videoPoster, label: t('projects.trailer', 'Fragman') });
  }
  if (Array.isArray(project.extraVideos)) {
    project.extraVideos.forEach(v => {
      allMedia.push({ type: 'video', url: v.url, format: v.type, poster: v.poster, label: getLocalizedText(v.label) });
    });
  }
  (project.gallery || []).forEach(img => {
    allMedia.push({ type: 'image', url: img });
  });

  const isCurrentItemVideo = allMedia[currentImageIndex]?.type === 'video';

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allMedia.length - 1 ? 0 : prev + 1
    );
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allMedia.length - 1 : prev - 1
    );
  };

  // Video thumbnail URL'sini oluşturan yardımcı fonksiyon
  const getYoutubeVideoId = (url) => {
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname.includes('youtu.be')) {
        return parsedUrl.pathname.replace('/', '');
      }
      if (parsedUrl.pathname.includes('/embed/')) {
        return parsedUrl.pathname.split('/embed/')[1].split('/')[0];
      }
      return parsedUrl.searchParams.get('v') || parsedUrl.pathname.split('/').pop();
    } catch (error) {
      return url.split('/').pop();
    }
  };

  const getVideoThumbnail = (videoUrl) => {
    const videoId = getYoutubeVideoId(videoUrl);
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getVideoUrl = (url) => {
    const videoId = getYoutubeVideoId(url);
    return `https://www.youtube.com/embed/${videoId}?controls=1&rel=0`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const getLocalizedList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    const list = value[currentLang] || value.tr || value.en;
    return Array.isArray(list) ? list : [];
  };

  const roles = getLocalizedList(project?.details?.roller);
  const features = getLocalizedList(project?.details?.özellikler);

  const year = project.date ? new Date(project.date).getFullYear() : '';
  const platform = getLocalizedText(project?.details?.platform, '');
  const metaLine = [project.technologies?.[0], platform, year]
    .filter(Boolean)
    .map(v => String(v).toUpperCase())
    .join(' · ');

  const statBoxes = [
    project?.details?.contributionPeriod
      ? [getLocalizedText({ tr: 'Katkı Dönemi', en: 'Contribution Period' }), getLocalizedText(project.details.contributionPeriod)]
      : [t('projects.developmentTime', 'Geliştirme Süresi'), getLocalizedText(project?.details?.geliştirmeSüresi, '-')],
    [t('projects.platform', 'Platform'), platform || '-'],
    [t('projects.status', 'Durum'), getLocalizedText(project?.details?.durumu, '-')],
    [t('projects.teamSize', 'Ekip Büyüklüğü'), getLocalizedText(project?.details?.ekipBüyüklüğü, '-')]
  ].filter(([, value]) => value !== '-');

  const gameUrlLabel = getLocalizedText(project.gameUrlLabel, t('projects.playGame', 'Oyunu Oyna'));

  return (
    <section className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines pt-20 pb-10 md:pt-20 md:pb-14">
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[10%] right-[8%] w-3 h-3 bg-arcade-pink"></div>
      <div className="absolute bottom-[12%] left-[5%] w-2.5 h-2.5 bg-arcade-green"></div>
      <div className="absolute top-[55%] right-[16%] w-2 h-2 bg-arcade-yellow animate-pulse"></div>

      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* üst bar */}
        <div className="flex items-center justify-between mb-10">
          <motion.button
            onClick={handleGoBack}
            className="font-pixel text-[10px] md:text-xs text-arcade-cyan border border-arcade-cyan/60 px-4 py-3 rounded hover:bg-arcade-cyan/10 transition-colors"
            variants={itemVariants}
            whileHover={{ x: -4 }}
            whileTap={{ scale: 0.95 }}
          >
            ◀ {t('projects.goBack', 'Geri Dön').toUpperCase()}
          </motion.button>

          {project.gameUrl && (
            <motion.a
              href={project.gameUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-block font-pixel text-[10px] md:text-xs bg-arcade-cyan text-arcade-bg px-5 py-3 rounded border border-arcade-cyan shadow-[0_0_14px_rgba(77,232,244,0.5)] hover:bg-arcade-cyan/80 transition-colors"
              variants={itemVariants}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              ▶ {String(gameUrlLabel).toUpperCase()}
            </motion.a>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-8 lg:gap-10 items-start">
          {/* Sol - Medya paneli */}
          <motion.div variants={itemVariants} className="min-w-0">
            <div className="relative border-2 border-arcade-cyan rounded-lg bg-arcade-panel/60 shadow-[0_0_18px_rgba(77,232,244,0.25)]">
              <span className="absolute -top-4 left-5 font-pixel text-[10px] md:text-xs text-arcade-bg bg-arcade-cyan px-4 py-2 z-10">
                LEVEL {String(levelNumber).padStart(2, '0')}
              </span>

              <div className="relative aspect-video w-full overflow-hidden rounded-t-lg bg-black">
                <AnimatePresence mode="wait">
                  {isCurrentItemVideo ? (
                    <motion.div
                      key={allMedia[currentImageIndex].url}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full relative z-0"
                    >
                      {allMedia[currentImageIndex].format === 'hls' ? (
                        <SteamVideo
                          src={allMedia[currentImageIndex].url}
                          poster={allMedia[currentImageIndex].poster}
                          title={`${getLocalizedText(project.title)} — ${allMedia[currentImageIndex].label}`}
                          steamUrl={project.gameUrl}
                        />
                      ) : <iframe
                        src={getVideoUrl(allMedia[currentImageIndex].url)}
                        title={`${getLocalizedText(project.title)} Gameplay`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />}
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`img-${currentImageIndex}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-arcade-bg w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={allMedia[currentImageIndex]?.url}
                        alt={`${getLocalizedText(project.title)} - Görsel ${currentImageIndex}`}
                        className="max-w-full max-h-full object-contain"
                        loading="eager"
                        decoding="async"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* küçük görseller */}
              {allMedia.length > 1 && (
                <div className="p-3 border-t border-arcade-cyan/20">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      onClick={previousImage}
                      className="font-pixel text-arcade-cyan text-xs px-2 py-2 border border-arcade-cyan/40 rounded hover:bg-arcade-cyan/10 transition-colors shrink-0"
                      aria-label="Önceki görsel"
                    >
                      ◀
                    </button>

                    <div className="min-w-0 flex-1 overflow-x-auto hide-scrollbar">
                      <div className="grid grid-flow-col auto-cols-max gap-2">
                        {allMedia.map((item, index) => (
                          <div
                            key={index}
                            className={`w-14 h-14 md:w-16 md:h-16 overflow-hidden cursor-pointer relative border rounded ${
                              currentImageIndex === index
                                ? 'border-arcade-cyan shadow-[0_0_8px_rgba(77,232,244,0.6)]'
                                : 'border-white/10 opacity-60 hover:opacity-100'
                            } transition-all`}
                            onClick={() => setCurrentImageIndex(index)}
                          >
                            <img
                              src={item.type === 'video' ? (item.poster || getVideoThumbnail(item.url)) : item.url}
                              alt={`${getLocalizedText(project.title)} - Küçük Görsel ${index}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            {item.type === 'video' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <span className="text-arcade-cyan text-xs">▶</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={nextImage}
                      className="font-pixel text-arcade-cyan text-xs px-2 py-2 border border-arcade-cyan/40 rounded hover:bg-arcade-cyan/10 transition-colors shrink-0"
                      aria-label="Sonraki görsel"
                    >
                      ▶
                    </button>
                  </div>
                </div>
              )}
            </div>

          </motion.div>

          {/* Sağ - Proje bilgileri */}
          <div className="min-w-0 break-words">
            {metaLine && (
              <motion.p
                variants={itemVariants}
                className="font-pixel text-arcade-yellow text-[10px] md:text-sm tracking-wider mb-4"
              >
                {metaLine}
              </motion.p>
            )}

            <motion.h1
              variants={itemVariants}
              className="font-pixel text-arcade-cyan text-lg sm:text-2xl xl:text-3xl leading-relaxed [overflow-wrap:anywhere] mb-6 drop-shadow-[0_0_12px_rgba(77,232,244,0.45)]"
            >
              {String(getLocalizedText(project.title)).toUpperCase()}
            </motion.h1>

            {roles.length > 0 && (
              <motion.div variants={itemVariants} className="flex flex-wrap gap-2 mb-6">
                {roles.map((rol) => (
                  <span
                    key={rol}
                    className="max-w-full break-words font-mono text-[11px] md:text-xs text-arcade-pink border border-arcade-pink px-3 py-1.5 tracking-wider uppercase"
                  >
                    {rol}
                  </span>
                ))}
              </motion.div>
            )}

            <motion.p
              variants={itemVariants}
              className="font-mono text-sm md:text-base text-gray-200 leading-8 mb-4"
            >
              {getLocalizedText(project.description)}
            </motion.p>

            {project.longDescription && (
              <motion.p
                variants={itemVariants}
                className="font-mono text-xs md:text-sm text-arcade-purple leading-7 mb-8"
              >
                {getLocalizedText(project.longDescription)}
              </motion.p>
            )}

            {/* istatistik kutuları */}
            <motion.div variants={itemVariants} className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,140px),1fr))] gap-3 mb-8">
              {statBoxes.map(([label, value]) => (
                <div
                  key={label}
                  className="border border-arcade-purple/25 bg-arcade-panel/60 px-3 py-3 text-center"
                >
                  <p className="font-mono text-sm md:text-base text-arcade-pink font-bold mb-1 break-words">
                    {value}
                  </p>
                  <p className="font-mono text-[9px] md:text-[10px] text-arcade-purple tracking-widest uppercase">
                    {label}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* case study */}
            {project.caseStudy && (
              <motion.div variants={itemVariants} className="mb-8 border border-arcade-cyan/25 bg-arcade-panel/50 p-5">
                <h2 className="font-pixel text-arcade-pink text-xs md:text-sm mb-4">
                  {t('projects.caseStudy', 'Case Study').toUpperCase()}
                </h2>
                <div className="space-y-4">
                  {[
                    ['problem', t('projects.problem', 'Problem')],
                    ['solution', t('projects.solution', 'Solution')],
                    ['impact', t('projects.impact', 'Impact')]
                  ].map(([key, label]) => (
                    <div key={key}>
                      <h3 className="font-mono text-xs font-bold text-arcade-cyan tracking-widest uppercase mb-1">
                        ▶ {label}
                      </h3>
                      <p className="font-mono text-xs md:text-sm text-gray-300 leading-6">
                        {getLocalizedText(project.caseStudy[key])}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* özellikler */}
            {features.length > 0 && (
              <motion.div variants={itemVariants} className="mb-8">
                <h3 className="font-pixel text-arcade-pink text-xs md:text-sm mb-4">
                  {t('projects.features', 'Özellikler').toUpperCase()}
                </h3>
                <ul className="space-y-2">
                  {features.map((ozellik) => (
                    <li key={ozellik} className="font-mono text-xs md:text-sm text-gray-200 flex items-start gap-3">
                      <span className="text-arcade-cyan mt-0.5">▶</span>
                      <span>{ozellik}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* teknolojiler */}
            {Array.isArray(project.technologies) && project.technologies.length > 0 && (
              <motion.div variants={itemVariants} className="mb-8">
                <h3 className="font-pixel text-arcade-pink text-xs md:text-sm mb-4">
                  {t('projects.technologies', 'Teknolojiler').toUpperCase()}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="max-w-full break-words font-mono text-[11px] md:text-xs text-arcade-cyan border border-arcade-cyan/60 bg-arcade-panel/40 px-3 py-1.5 tracking-wider hover:bg-arcade-cyan/10 transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* mobil oyna butonu */}
            {project.gameUrl && (
              <motion.a
                variants={itemVariants}
                href={project.gameUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="md:hidden inline-block font-pixel text-[10px] bg-arcade-cyan text-arcade-bg px-5 py-3 rounded border border-arcade-cyan shadow-[0_0_14px_rgba(77,232,244,0.5)]"
              >
                ▶ {String(gameUrlLabel).toUpperCase()}
              </motion.a>
            )}
          </div>
        </div>
      </motion.div>

      {/* Özel scroll çubuğu için stil */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}

export default ProjectDetail;
